import { useState, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useGetContactCollectionByFirebaseEventIdOrCreateOneQuery,
  useCreateContactsWithoutTagsMutation,
  useGetTelemetryDataForContactCollectionGuestQuery,
  useGetContactsByIdsGuestListQuery,
  useUpdateContactGuestListMutation,
} from '@graphql/generated';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useContactCollectorGuestTelemetry } from '../guest/ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../guest';
import { useToast } from '@withjoy/joykit';
import { normalizeContactData } from '../utils';

export interface ContactInputFields
  extends Readonly<{
    firstName: string;
    lastName: string;
    envelopeNameOverride?: string;
    phoneNumber?: string;
    email?: string;
    address1?: string;
    address2?: string;
    postalCode?: string;
    city?: string;
    region?: string;
    country?: string;
    countryCode?: string;
    validated: boolean;
    legacyAddressString?: string;
  }> {}

export type Contacts = { contacts: ContactInputFields[] };

export const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  envelopeNameOverride: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: 'United States',
  countryCode: 'US',
  validated: false,
  legacyAddressString: '',
};

export const initialValues: Contacts = {
  contacts: [contactInitialValue],
};

const yupSchema = (tRequired: string, tInvalidEmail: string) =>
  Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().test('first-element-first-name-required', tRequired, function (this, value) {
            if (this.path === 'contacts[0].firstName' && !value) {
              return false;
            }
            return true;
          }),
          lastName: Yup.string().test('first-element-last-name-required', tRequired, function (this, value) {
            if (this.path === 'contacts[0].lastName' && !value) {
              return false;
            }
            return true;
          }),
          envelopeNameOverride: Yup.string(),
          phoneNumber: Yup.string(),
          email: Yup.string().email(tInvalidEmail),
          address1: Yup.string(),
          address2: Yup.string(),
          postalCode: Yup.string(),
          city: Yup.string(),
          region: Yup.string(),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean(),
        }),
      ),
  });

export const useContactCollectorIframeController = (firebaseEventId: string, contactId?: string) => {
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const [phoneInputResetKey, setPhoneInputResetKey] = useState(0);
  const { trackError } = useContactCollectorGuestTelemetry();
  const routeHelpers = useRouterHelper();
  const { tq } = useTranslation('contactCollector');
  const { toast } = useToast();
  const isUpdateMode = !!contactId;

  const { data, loading, error } = useGetContactCollectionByFirebaseEventIdOrCreateOneQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      firebaseEventId,
    },
    onError: error => trackError('Query', error),
  });

  const eventId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.eventId;

  const {
    data: contactData,
    loading: isContactDataLoading,
    error: contactDataError,
  } = useGetContactsByIdsGuestListQuery({
    variables: {
      ids: [contactId!],
    },
    batchMode: 'fast',
    skip: !isUpdateMode,
    onError: error => trackError('Query', error),
  });

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!,
    },
    skip: !eventId,
    onError: error => trackError('Query', error),
  });

  const initialContactData = contactData?.getContactsByIds?.[0];

  const initialValuesForUpdate: Contacts = useMemo(
    () => ({
      contacts: [normalizeContactData(initialContactData)],
    }),
    [initialContactData],
  );

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();

  const [updateContactMutation, { loading: isUpdateContactMutationLoading, error: updateContactMutationError }] = useUpdateContactGuestListMutation();

  const formik = useFormik<Contacts>({
    enableReinitialize: isUpdateMode,
    initialValues: isUpdateMode ? initialValuesForUpdate : initialValues,
    validationSchema: yupSchema(tq`Required`, tq`Invalid Email`),
    validateOnMount: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        if (isUpdateMode) {
          // Update existing contact
          const contactCollectionId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.id;

          if (!contactCollectionId) {
            throw Error('No Collection Id provided');
          }
          const contact = values.contacts[0];
          const hadPhoneNumberBefore = !!initialContactData?.phoneNumber?.[0]?.number;
          const hasPhoneNumberNow = !!contact.phoneNumber;
          const hadAddress1Before = !!initialContactData?.household?.address?.address1;
          const hasAddress1Now = !!contact.address1;

          const mutationData = {
            address:
              hadAddress1Before || hasAddress1Now
                ? {
                    address1: contact.address1 ?? '',
                    address2: contact.address2,
                    city: contact.city ?? '',
                    country: contact.country ?? '',
                    countryCode: contact.countryCode ?? '',
                    postalCode: contact.postalCode ?? '',
                    region: contact.region ?? '',
                    validated: !!contact.validated,
                  }
                : undefined,
            contact: {
              firebaseEventId,
              eventId,
              email: (contact.email ?? '').trim(),
              firstName: contact.firstName.trim(),
              lastName: contact.lastName.trim(),
              contactCollectionId,
              householdDisplayName: contact.envelopeNameOverride?.trim(),
            },
            phoneNumbers:
              hadPhoneNumberBefore || hasPhoneNumberNow
                ? [
                    {
                      allowSMS: true,
                      mobile: true,
                      number: (contact.phoneNumber ?? '').trim(),
                    },
                  ]
                : undefined,
          };
          await updateContactMutation({ variables: { id: contactId, data: mutationData } });
          setE164PhoneNumber({ 0: '' });
          formikHelpers.resetForm();
          setPhoneInputResetKey(prev => prev + 1);
          routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path);
          toast(tq`Contact updated successfully.`, { position: 'bottom-center' });
        } else {
          // Create new contact
          const contactCollectionId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.id;

          if (!contactCollectionId) {
            throw Error('No Collection Id provided');
          }

          const mutationData = values.contacts.map((contact, index) => ({
            address:
              // we do not want to save addresses from household members which are not the head of the household
              // also since address is optional we are not saving partial address, we save it only when it's complete
              index === 0 && contact.address1 && contact.city && contact.country && contact.countryCode && contact.postalCode && contact.region
                ? {
                    address1: contact.address1,
                    address2: contact.address2,
                    city: contact.city,
                    country: contact.country,
                    countryCode: contact.countryCode,
                    postalCode: contact.postalCode,
                    region: contact.region,
                    validated: contact.validated,
                  }
                : undefined,
            contact: {
              contactCollectionId,
              firebaseEventId,
              eventId,
              email: contact.email ? contact.email.trim() : undefined,
              firstName: contact.firstName.trim(),
              lastName: contact.lastName.trim(),
              party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined,
              householdDisplayName: contact.envelopeNameOverride?.trim(),
            },
            phoneNumbers: contact.phoneNumber
              ? [
                  {
                    allowSMS: true,
                    mobile: true,
                    number: contact.phoneNumber.trim(),
                  },
                ]
              : undefined,
          }));
          await createContactsMutation({ variables: { data: mutationData } });
          setE164PhoneNumber({ 0: '' });
          formikHelpers.resetForm();
          setPhoneInputResetKey(prev => prev + 1);
          routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path);
          toast(values.contacts.length > 1 ? tq`Contacts created successfully.` : tq`Contact created successfully.`, { position: 'bottom-center' });
        }
      } catch (error) {
        trackError('Mutation', error);
      }
    },
  });

  return {
    showErrorPage: !!error || !!contactDataError,
    isLoading: loading || isTelemetryQueryLoading || isContactDataLoading,
    formik,
    isMutationLoading: isCreateContactsMutationLoading || isUpdateContactMutationLoading,
    hasMutationError: !!createContactsMutationError || !!updateContactMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? '',
    },
    e164PhoneNumber,
    setE164PhoneNumber,
    phoneInputResetKey,
  };
};
